import type { CnpjLookup, CompanySummary, CreateCompanyBody } from '@obras/shared';
import { maskCnpj, maskPhone, stripCnpj } from '@obras/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Building2, Check, CircleAlert, Download, Loader2 } from 'lucide-react';
import { useState, type FormEvent } from 'react';

import { Button } from '../../components/ui/Button.js';
import { Checkbox, TextAreaField, TextField } from '../../components/ui/Field.js';
import { FormError } from '../../components/ui/Feedback.js';
import { Modal } from '../../components/ui/Modal.js';
import { api, ApiError } from '../../lib/api.js';
import { useCompanyByCnpj } from './api.js';

const ICON = { strokeWidth: 1.5 } as const;

const VAZIO = {
  legalName: '',
  tradeName: '',
  cnpj: '',
  email: '',
  phone: '',
  address: '',
  notes: '',
  ativa: true,
};

type Campos = typeof VAZIO;

/**
 * Cadastro e edição de empresa.
 *
 * Mora aqui, e não na página de empresas, porque o cadastro de obra abre este
 * mesmo formulário quando a empresa contratada ainda não existe — e mandar a
 * pessoa para outra tela no meio de um cadastro faz perder o que já foi
 * digitado.
 */
export function CompanyFormModal({
  open,
  editando,
  onClose,
  onSaved,
}: {
  open: boolean;
  editando?: CompanySummary | null;
  onClose: () => void;
  /** Quem abriu daqui de dentro precisa do id para já selecionar a empresa. */
  onSaved?: (empresa: CompanySummary) => void;
}) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<Campos>(VAZIO);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [pronto, setPronto] = useState(false);
  const [aviso, setAviso] = useState<string | null>(null);

  const salvar = useMutation({
    mutationFn: (body: CreateCompanyBody) =>
      editando
        ? api.patch<CompanySummary>(`/empresas/${editando.id}`, body)
        : api.post<CompanySummary>('/empresas', body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['empresas'] }),
  });

  const consulta = useMutation({
    mutationFn: (cnpj: string) => api.get<CnpjLookup>(`/empresas/consulta-cnpj/${cnpj}`),
  });

  /*
   * Carrega os campos da empresa em edição na primeira renderização com ela.
   * O mesmo padrão das demais telas de edição, e sem efeito para não desenhar
   * um quadro com o formulário vazio antes de preencher.
   */
  if (open && !pronto) {
    setForm(
      editando
        ? {
            legalName: editando.legalName,
            tradeName: editando.tradeName ?? '',
            cnpj: editando.cnpj ? maskCnpj(editando.cnpj) : '',
            email: editando.email ?? '',
            phone: editando.phone ? maskPhone(editando.phone) : '',
            address: editando.address ?? '',
            notes: editando.notes ?? '',
            ativa: editando.status === 'ativa',
          }
        : VAZIO,
    );
    setPronto(true);
  }

  const set = <K extends keyof Campos>(key: K, value: Campos[K]): void =>
    setForm((current) => ({ ...current, [key]: value }));

  // Consultar meio CNPJ não devolve nada; o botão espera os quatorze.
  const cnpjLimpo = stripCnpj(form.cnpj);
  const cnpjCompleto = cnpjLimpo.length === 14;

  /*
   * O CNPJ digitado já está no órgão?
   *
   * Perguntado assim que os quatorze caracteres entram, e não só ao salvar: o
   * servidor recusaria de qualquer forma, mas aí a pessoa já teria preenchido o
   * formulário inteiro de uma empresa que existe. Na edição, o próprio CNPJ da
   * empresa não conta — ela não é duplicata de si mesma.
   */
  const existente = useCompanyByCnpj(
    open && cnpjCompleto && cnpjLimpo !== stripCnpj(editando?.cnpj ?? '') ? cnpjLimpo : null,
  );
  const duplicada = existente.data && existente.data.id !== editando?.id ? existente.data : null;

  /**
   * Puxa da Receita e preenche o que estiver em branco.
   *
   * **Não sobrescreve o que já foi digitado.** Quem corrigiu a razão social à
   * mão porque o contrato traz outra grafia não pode perder a correção por ter
   * clicado aqui depois.
   */
  async function puxarDaReceita(): Promise<void> {
    setAviso(null);
    setFormError(null);

    try {
      const dados = await consulta.mutateAsync(stripCnpj(form.cnpj));

      setForm((atual) => ({
        ...atual,
        cnpj: maskCnpj(dados.cnpj),
        legalName: atual.legalName || dados.legalName,
        tradeName: atual.tradeName || (dados.tradeName ?? ''),
        email: atual.email || (dados.email ?? ''),
        phone: atual.phone || (dados.phone ?? ''),
        address:
          atual.address || [dados.address, dados.city, dados.state].filter(Boolean).join(' — '),
      }));

      /*
       * A situação cadastral não impede o cadastro — impedir seria o sistema
       * decidindo com quem o município contrata. Mas empresa baixada ou suspensa
       * é coisa que quem cadastra precisa ver antes de salvar.
       */
      if (dados.situation && dados.situation.toUpperCase() !== 'ATIVA') {
        setAviso(`Situação cadastral na Receita: ${dados.situation}.`);
      }
    } catch (error) {
      setFormError(
        error instanceof ApiError
          ? error.message
          : 'Não foi possível consultar. Preencha os dados manualmente.',
      );
    }
  }

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setFieldErrors({});
    setFormError(null);

    const orUndefined = (value: string): string | undefined =>
      value.trim() === '' ? undefined : value;

    try {
      const salva = await salvar.mutateAsync({
        legalName: form.legalName.trim(),
        tradeName: orUndefined(form.tradeName),
        cnpj: orUndefined(form.cnpj),
        email: orUndefined(form.email),
        phone: orUndefined(form.phone),
        address: orUndefined(form.address),
        notes: orUndefined(form.notes),
        /*
         * Na edição vai o que a caixa diz. Antes ia `ativa` sempre, e trocar o
         * telefone de uma empresa inativa a reativava em silêncio.
         */
        status: form.ativa ? 'ativa' : 'inativa',
      });

      onSaved?.(salva);
      fechar();
    } catch (error) {
      if (error instanceof ApiError) {
        setFieldErrors(error.fieldErrors());
        setFormError(error.details ? 'Verifique os campos destacados.' : error.message);
      } else {
        setFormError('Não foi possível salvar. Tente novamente.');
      }
    }
  }

  function fechar(): void {
    setForm(VAZIO);
    setPronto(false);
    setFieldErrors({});
    setFormError(null);
    setAviso(null);
    consulta.reset();
    onClose();
  }

  /** Quem abriu de dentro do cadastro de obra fica com a empresa que já existe. */
  function usarExistente(): void {
    if (!duplicada) return;
    onSaved?.(duplicada);
    fechar();
  }

  return (
    <Modal
      open={open}
      onClose={fechar}
      title={editando ? 'Editar empresa' : 'Nova empresa'}
      description="Empresa contratada para execução de obras."
      footer={
        <>
          <Button variant="ghost" onClick={fechar} disabled={salvar.isPending}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            form="company-form"
            type="submit"
            loading={salvar.isPending}
            /* Salvar com CNPJ repetido só devolveria a mesma recusa do servidor. */
            disabled={duplicada !== null}
            icon={<Check {...ICON} size={16} />}
          >
            {editando ? 'Salvar' : 'Cadastrar'}
          </Button>
        </>
      }
    >
      <form
        id="company-form"
        onSubmit={(event) => void handleSubmit(event)}
        className="flex flex-col gap-4"
        noValidate
      >
        {formError ? <FormError>{formError}</FormError> : null}

        {duplicada ? (
          <div
            role="status"
            className="flex items-start gap-2 bg-warning-soft px-3.5 py-3 text-[13px] text-warning"
          >
            <Building2 {...ICON} size={16} className="mt-px shrink-0" aria-hidden />
            <div className="flex min-w-0 flex-col gap-2">
              <p>
                Este CNPJ já está cadastrado:{' '}
                <strong className="font-semibold">
                  {duplicada.tradeName ?? duplicada.legalName}
                </strong>
                {duplicada.status === 'inativa' ? ' (inativa)' : ''}.{' '}
                {duplicada.status === 'inativa'
                  ? 'Reative-a na tela de empresas em vez de cadastrar outra.'
                  : onSaved
                    ? 'Use a empresa existente em vez de cadastrar outra.'
                    : 'Edite o cadastro existente em vez de criar outro.'}
              </p>
              {/* Inativa não entra no seletor de contratada; oferecer seria selecionar o invisível. */}
              {onSaved && duplicada.status === 'ativa' ? (
                <Button size="sm" variant="surface" className="self-start" onClick={usarExistente}>
                  Usar esta empresa
                </Button>
              ) : null}
            </div>
          </div>
        ) : null}

        {aviso ? (
          <p
            role="status"
            className="flex items-start gap-2 bg-warning-soft px-3.5 py-3 text-[13px] text-warning"
          >
            <CircleAlert {...ICON} size={16} className="mt-px shrink-0" aria-hidden />
            {aviso}
          </p>
        ) : null}

        {/*
         * O CNPJ vem primeiro, e não no meio do formulário: é por ele que se
         * preenche o resto. Deixá-lo depois da razão social convidava a digitar
         * tudo à mão antes de perceber que dava para consultar.
         */}
        {/*
         * O `flex-1` vai no invólucro, e não no `TextField`: o `className` dele
         * chega ao `<input>`, e dentro de uma linha flex o campo colapsava.
         */}
        <div className="flex items-end gap-2">
          <div className="min-w-0 flex-1">
            <TextField
              label="CNPJ"
              mono
              /*
               * `text`, e não `numeric`: o CNPJ alfanumérico tem letra, e um
               * teclado só de números no celular tornaria impossível digitá-lo.
               */
              inputMode="text"
              placeholder="00.000.000/0000-00"
              value={form.cnpj}
              onChange={(event) => set('cnpj', maskCnpj(event.target.value))}
              hint="Aceita também o formato alfanumérico."
              {...(fieldErrors['cnpj'] ? { error: fieldErrors['cnpj'] } : {})}
            />
          </div>

          <Button
            onClick={() => void puxarDaReceita()}
            disabled={!cnpjCompleto || consulta.isPending}
            title={
              cnpjCompleto
                ? 'Buscar razão social, endereço e telefone na Receita'
                : 'Informe o CNPJ completo para consultar'
            }
            icon={
              consulta.isPending ? (
                <Loader2 {...ICON} size={16} className="animate-spin" />
              ) : (
                <Download {...ICON} size={16} />
              )
            }
            /* `mb-6` alinha com o campo, que tem rótulo em cima e dica embaixo. */
            className="mb-6 shrink-0"
          >
            {consulta.isPending ? 'Consultando…' : 'Buscar dados'}
          </Button>
        </div>

        <TextField
          label="Razão social"
          required
          value={form.legalName}
          onChange={(event) => set('legalName', event.target.value)}
          {...(fieldErrors['legalName'] ? { error: fieldErrors['legalName'] } : {})}
        />

        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            label="Nome fantasia"
            value={form.tradeName}
            onChange={(event) => set('tradeName', event.target.value)}
            {...(fieldErrors['tradeName'] ? { error: fieldErrors['tradeName'] } : {})}
          />
          <TextField
            label="Telefone"
            mono
            inputMode="tel"
            placeholder="(65) 3831-1200"
            value={form.phone}
            onChange={(event) => set('phone', maskPhone(event.target.value))}
            {...(fieldErrors['phone'] ? { error: fieldErrors['phone'] } : {})}
          />
        </div>

        <TextField
          label="E-mail"
          type="email"
          value={form.email}
          onChange={(event) => set('email', event.target.value)}
          {...(fieldErrors['email'] ? { error: fieldErrors['email'] } : {})}
        />

        <TextField
          label="Endereço"
          value={form.address}
          onChange={(event) => set('address', event.target.value)}
          {...(fieldErrors['address'] ? { error: fieldErrors['address'] } : {})}
        />

        <TextAreaField
          label="Observações"
          rows={3}
          value={form.notes}
          onChange={(event) => set('notes', event.target.value)}
          {...(fieldErrors['notes'] ? { error: fieldErrors['notes'] } : {})}
        />

        {/*
         * Só na edição: empresa nova nasce ativa. Inativar é o caminho para a
         * empresa com obra vinculada, que não pode ser removida — sai da lista
         * de escolha do cadastro de obra sem apagar o histórico.
         */}
        {editando ? (
          <Checkbox
            label="Empresa ativa"
            hint="Inativa, deixa de aparecer na escolha de contratada ao cadastrar obra. O histórico fica."
            checked={form.ativa}
            onChange={(event) => set('ativa', event.target.checked)}
          />
        ) : null}
      </form>
    </Modal>
  );
}
